import { useCallback, useMemo, useState } from "react"
import {
  Button,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import ButtonTooltip from "components/Common/ButtonTooltip"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import {
  useCompanyPads,
  useCreateCompanyPad,
  useEditCompanyPad,
  useDeleteCompanyPad,
} from "hooks/useCompanyPad"
import { useToastMessage } from "hooks/useToastMessage"

const TEMPLATE_OPTIONS = [
  { value: "classic", label: "Classic (Aurobil default look)" },
  { value: "modern", label: "Modern (accent header band)" },
  { value: "formal", label: "Formal (centered, serif)" },
  { value: "compact", label: "Compact (dense)" },
]

const FONT_OPTIONS = [
  { value: "montserrat", label: "Montserrat (sans)" },
  { value: "dejavuserif", label: "DejaVu Serif" },
  { value: "dejavusans", label: "DejaVu Sans" },
  { value: "freeserif", label: "Free Serif" },
]

const LAYOUT_OPTIONS = [
  { value: "letter", label: "Letter (narrative flow)" },
  { value: "tabular", label: "Tabular (rows + tables)" },
  { value: "box", label: "Box (card-grid layout)" },
]

const SIGNATURE_STYLE_OPTIONS = [
  { value: "auto", label: "Auto (varied per quote)" },
  { value: "s1", label: "Split — two columns" },
  { value: "s2", label: "Right-stacked" },
  { value: "s3", label: "Centered" },
  { value: "s4", label: "Boxed card" },
  { value: "s5", label: "Formal two-column" },
  { value: "s6", label: "Left-stacked" },
]

const TEMPLATE_TOKENS = [
  "{chassis_no}",
  "{engine_no}",
  "{model}",
  "{color}",
  "{cc}",
  "{fuel_type}",
]

const EMPTY_PAD = {
  name: "",
  short_name: "",
  phone: "",
  email: "",
  address: "",
  template: "classic",
  accent_color: "#ED1C24",
  font_family: "montserrat",
  pad_layout: "tabular",
  default_validity_days: 30,
  default_reference_prefix: "",
  date_format: "",
  description_template: "",
  payment_terms_template: "",
  subject: "",
  intro_message: "",
  closing_message: "",
  customer_signature_label: "",
  signature_style: "auto",
  is_active: true,
}

const padLayoutBadgeColor = layout => {
  if (layout === "letter") return "info"
  if (layout === "box") return "warning"
  return "secondary"
}

const CompanyPadList = () => {
  document.title = "Company Pads | Aurobil Cars"

  const { handleError } = useToastMessage()

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search ?? "",
    }),
    [params]
  )

  const { data, isLoading, refetch, isRefetching } = useCompanyPads(queryParams)
  const { mutateAsync: createPad, isPending: creating } = useCreateCompanyPad()
  const { mutateAsync: editPad, isPending: editing } = useEditCompanyPad()
  const { mutateAsync: deletePad, isPending: isDeleting } = useDeleteCompanyPad()

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [patches, setPatches] = useState({})
  const [deleteTarget, setDeleteTarget] = useState(null)

  const mergePad = useCallback(
    pad => {
      const patch = patches[pad.id]
      return patch ? { ...pad, ...patch } : pad
    },
    [patches]
  )

  const fetchedData = data?.data?.data ?? data?.data ?? []
  const pads = fetchedData.map(mergePad)
  const paginationInfo = data?.data

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EMPTY_PAD,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").nullable(),
      default_validity_days: Yup.number()
        .typeError("Default validity must be a number")
        .integer("Default validity must be a whole number")
        .min(1, "Default validity must be at least 1 day")
        .max(365, "Default validity cannot exceed 365 days")
        .nullable(),
    }),
    onSubmit: async values => {
      try {
        if (editId) {
          const payload = { id: editId, ...values }
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.log("[CompanyPad] PATCH /company-pads", payload)
          }
          const res = await editPad(payload)
          const returned = res?.data ?? {}
          setPatches(prev => ({
            ...prev,
            [editId]: {
              pad_layout: returned.pad_layout ?? values.pad_layout,
              default_validity_days:
                returned.default_validity_days ?? values.default_validity_days,
              default_reference_prefix:
                returned.default_reference_prefix ??
                values.default_reference_prefix,
              date_format: returned.date_format ?? values.date_format,
              description_template:
                returned.description_template ?? values.description_template,
              payment_terms_template:
                returned.payment_terms_template ??
                values.payment_terms_template,
              subject: returned.subject ?? values.subject,
              intro_message: returned.intro_message ?? values.intro_message,
              closing_message: returned.closing_message ?? values.closing_message,
              customer_signature_label:
                returned.customer_signature_label ??
                values.customer_signature_label,
              signature_style:
                returned.signature_style ?? values.signature_style,
              template: returned.template ?? values.template,
              accent_color: returned.accent_color ?? values.accent_color,
              font_family: returned.font_family ?? values.font_family,
            },
          }))
          toast.success("Company pad updated.")
        } else {
          await createPad(values)
          toast.success("Company pad created.")
        }
        closeModal()
      } catch (error) {
        handleError(error, "Failed to save company pad.")
      }
    },
  })

  const openCreate = () => {
    setEditId(null)
    formik.resetForm({ values: EMPTY_PAD })
    setModalOpen(true)
  }

  const openEdit = pad => {
    setEditId(pad.id)
    const merged = mergePad(pad)
    formik.resetForm({
      values: {
        name: merged.name ?? "",
        short_name: merged.short_name ?? "",
        phone: merged.phone ?? "",
        email: merged.email ?? "",
        address: merged.address ?? "",
        template: merged.template ?? "classic",
        accent_color: merged.accent_color ?? "#ED1C24",
        font_family: merged.font_family ?? "montserrat",
        pad_layout: merged.pad_layout ?? "tabular",
        default_validity_days: merged.default_validity_days ?? 30,
        default_reference_prefix: merged.default_reference_prefix ?? "",
        date_format: merged.date_format ?? "",
        description_template: merged.description_template ?? "",
        payment_terms_template: merged.payment_terms_template ?? "",
        subject: merged.subject ?? "",
        intro_message: merged.intro_message ?? "",
        closing_message: merged.closing_message ?? "",
        customer_signature_label: merged.customer_signature_label ?? "",
        signature_style: merged.signature_style ?? "auto",
        is_active: !!merged.is_active,
      },
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditId(null)
  }

  const handleDelete = async () => {
    if (!deleteTarget?.id) return
    try {
      await deletePad({ id: deleteTarget.id })
      toast.success("Company pad deleted.")
      setDeleteTarget(null)
    } catch (error) {
      handleError(error, "Failed to delete company pad.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Name",
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="fw-bold text-primary mb-1">
                {item?.name ?? "-"}
              </div>
              {item?.short_name && (
                <small className="text-muted d-block">
                  {item.short_name}
                </small>
              )}
              <div className="d-sm-none mt-1">
                <div className="small text-muted">
                  <i className="mdi mdi-phone-outline me-1" />
                  {item?.phone || "-"}
                </div>
                <div className="small text-muted">
                  <i className="mdi mdi-email-outline me-1" />
                  {item?.email || "-"}
                </div>
                {item?.address && (
                  <div className="small text-muted mt-1">
                    {item.address}
                  </div>
                )}
                <div className="mt-1">
                  <Badge
                    color={padLayoutBadgeColor(item?.pad_layout)}
                    className="text-capitalize me-1"
                  >
                    {item?.pad_layout || "tabular"}
                  </Badge>
                  <Badge
                    color={item?.is_active ? "success" : "secondary"}
                  >
                    {item?.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </div>
          )
        },
      },
      {
        header: "Contact",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              <div className="small">
                <i className="mdi mdi-phone-outline me-1 text-muted" />
                {item?.phone || "-"}
              </div>
              <div className="small">
                <i className="mdi mdi-email-outline me-1 text-muted" />
                {item?.email || "-"}
              </div>
            </div>
          )
        },
      },
      {
        header: "Address",
        meta: { className: "d-none d-md-table-cell" },
        cell: ({ row }) => (
          <div
            className="small text-muted"
            style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
          >
            {row.original?.address || "-"}
          </div>
        ),
      },
      {
        header: "Style",
        meta: { className: "d-none d-lg-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div
              className="d-inline-flex align-items-center gap-2"
              style={{ flexWrap: "wrap" }}
            >
              <span
                title={item?.accent_color}
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: "1px solid #ccc",
                  background: item?.accent_color || "#ED1C24",
                  flexShrink: 0,
                }}
              />
              <span className="text-capitalize small">
                {item?.template || "classic"}
              </span>
              <span className="text-muted small text-capitalize">
                · {item?.font_family || "montserrat"}
              </span>
            </div>
          )
        },
      },
      {
        header: "Layout",
        meta: { className: "d-none d-md-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <Badge
              color={padLayoutBadgeColor(item?.pad_layout)}
              className="text-capitalize"
            >
              {item?.pad_layout || "tabular"}
            </Badge>
          )
        },
      },
      {
        header: "Status",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <Badge color={item?.is_active ? "success" : "secondary"}>
              {item?.is_active ? "Active" : "Inactive"}
            </Badge>
          )
        },
      },
      {
        header: "Action",
        meta: { className: "table-col-action" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
              <li>
                <ButtonTooltip
                  type="edit"
                  btnId={`company-pad-edit-${item?.id}`}
                  onClick={() => openEdit(item)}
                />
              </li>
              <li>
                <ButtonTooltip
                  type="delete"
                  btnId={`company-pad-delete-${item?.id}`}
                  loading={isDeleting}
                  onClick={() => setDeleteTarget(item)}
                />
              </li>
            </ul>
          )
        },
      },
    ],
    [isDeleting, mergePad]
  )

  return (
    <PageWrapper title="Settings" breadcrumbItem="Company Pads">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total{" "}
            <span className="badge badge-soft-info">
              {paginationInfo?.total ?? 0}
            </span>
          </h5>
        </Col>
        <Col
          xs={6}
          className="d-flex justify-content-end align-items-center gap-1"
        >
          <ButtonTooltip
            type="refresh"
            btnId="refreshCompanyPadList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <Button color="primary" onClick={openCreate}>
            <i className="bx bx-plus" />
            <span className="d-none d-sm-inline ms-1">Add Pad</span>
          </Button>
          {hasActiveFilters() && (
            <Button color="danger" onClick={() => resetParams([], { page: 1 })}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search pad name, short name, phone..."
          />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={pads}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={queryParams.per_page}
        getRowKey={row => row.id}
      />

      <DeleteModal
        show={!!deleteTarget}
        onDeleteClick={handleDelete}
        loading={isDeleting}
        onCloseClick={() => setDeleteTarget(null)}
      />

      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <Form onSubmit={formik.handleSubmit}>
          <ModalHeader toggle={closeModal}>
            {editId ? "Edit Company Pad" : "Add Company Pad"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Name *</Label>
              <Input
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.name && !!formik.errors.name}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="invalid-feedback d-block">
                  {formik.errors.name}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Short Name (letterhead brand)</Label>
              <Input
                name="short_name"
                value={formik.values.short_name}
                onChange={formik.handleChange}
                placeholder="e.g. ROYAL CARS"
              />
            </FormGroup>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.email && !!formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="invalid-feedback d-block">
                  {formik.errors.email}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Address</Label>
              <Input
                type="textarea"
                name="address"
                rows={2}
                value={formik.values.address}
                onChange={formik.handleChange}
              />
            </FormGroup>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label>Header Template</Label>
                  <Input
                    type="select"
                    name="template"
                    value={formik.values.template}
                    onChange={formik.handleChange}
                  >
                    {TEMPLATE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label>Font</Label>
                  <Input
                    type="select"
                    name="font_family"
                    value={formik.values.font_family}
                    onChange={formik.handleChange}
                  >
                    {FONT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label>Signatory Footer (Supportive)</Label>
                  <Input
                    type="select"
                    name="signature_style"
                    value={formik.values.signature_style}
                    onChange={formik.handleChange}
                  >
                    {SIGNATURE_STYLE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    Footer signature layout for this pad's supportive quotations.
                  </small>
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label>Layout (Supportive)</Label>
                  <Input
                    type="select"
                    name="pad_layout"
                    value={formik.values.pad_layout}
                    onChange={formik.handleChange}
                  >
                    {LAYOUT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    Used when generating supportive quotations from this pad.
                  </small>
                </FormGroup>
              </div>
              <div className="col-md-3">
                <FormGroup>
                  <Label>Default Validity (days)</Label>
                  <Input
                    type="number"
                    name="default_validity_days"
                    min="1"
                    max="365"
                    value={formik.values.default_validity_days}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.default_validity_days &&
                      !!formik.errors.default_validity_days
                    }
                  />
                  {formik.touched.default_validity_days &&
                    formik.errors.default_validity_days && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.default_validity_days}
                      </div>
                    )}
                </FormGroup>
              </div>
              <div className="col-md-3">
                <FormGroup>
                  <Label>Ref. Prefix</Label>
                  <Input
                    type="text"
                    name="default_reference_prefix"
                    value={formik.values.default_reference_prefix}
                    onChange={formik.handleChange}
                    placeholder="e.g. RC-"
                  />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label>
                Date Format{" "}
                <small className="text-muted">(quotation PDF dates)</small>
              </Label>
              <Input
                type="text"
                name="date_format"
                value={formik.values.date_format}
                onChange={formik.handleChange}
                placeholder="e.g. F j, Y   or   d/m/Y   or   d-M-Y"
              />
              <small className="text-muted">
                PHP date tokens. Blank = default (August 27, 2026). Controls the
                quotation date &amp; validity date on this pad&apos;s PDFs.
              </small>
            </FormGroup>
            <FormGroup>
              <Label>
                Description Template{" "}
                <small className="text-muted">(supportive letters)</small>
              </Label>
              <Input
                type="textarea"
                name="description_template"
                rows={2}
                value={formik.values.description_template}
                onChange={formik.handleChange}
                placeholder={`e.g. Vehicle ${"{model}"} chassis ${"{chassis_no}"} ${"{cc}"} ${"{fuel_type}"} ${"{color}"}`}
              />
              <small className="text-muted">
                Tokens: {TEMPLATE_TOKENS.join(", ")}
              </small>
            </FormGroup>
            <FormGroup>
              <Label>
                Payment Terms Template{" "}
                <small className="text-muted">(supportive default)</small>
              </Label>
              <Input
                type="textarea"
                name="payment_terms_template"
                rows={2}
                value={formik.values.payment_terms_template}
                onChange={formik.handleChange}
                placeholder="Full payment must be made before vehicle delivery."
              />
              <small className="text-muted">
                Blank = every quotation uses &quot;Full payment must be made
                before vehicle delivery.&quot;
              </small>
            </FormGroup>
            <hr className="my-2" />
            <h6 className="text-muted text-uppercase small mb-2">
              Pad-Unique Content
            </h6>
            <p className="text-muted small mb-3">
              These values make this pad look different from other pads.
              Separate paragraphs with a blank line.
            </p>
            <FormGroup>
              <Label>Subject</Label>
              <Input
                type="text"
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                placeholder="e.g. Quotation for reconditioned vehicle"
              />
            </FormGroup>
            <FormGroup>
              <Label>Beginning Message</Label>
              <Input
                type="textarea"
                name="intro_message"
                rows={3}
                value={formik.values.intro_message}
                onChange={formik.handleChange}
                placeholder="Dear Sir,&#10;We are pleased to provide you with a quotation for the reconditioned vehicle(s) you are interested in. Please find the details below:"
              />
            </FormGroup>
            <FormGroup>
              <Label>
                Ending Message{" "}
                <small className="text-muted">
                  (supports basic HTML: &lt;strong&gt;&lt;em&gt;...&lt;/em&gt;&lt;/strong&gt;)
                </small>
              </Label>
              <Input
                type="textarea"
                name="closing_message"
                rows={4}
                value={formik.values.closing_message}
                onChange={formik.handleChange}
                placeholder={`Please do not hesitate to contact us if you have any questions or require further information. Our sales representatives are here to assist you in making the best choice.\n\nThank you for considering <strong><em>AUROBIL CARS</em></strong> for your reconditioned vehicle purchase. We look forward to serving your automotive needs.`}
              />
            </FormGroup>
            <FormGroup>
              <Label>Customer Signature Label</Label>
              <Input
                type="text"
                name="customer_signature_label"
                value={formik.values.customer_signature_label}
                onChange={formik.handleChange}
                placeholder="e.g. Customer Acceptance"
              />
            </FormGroup>
            <FormGroup>
              <Label>Accent Color</Label>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="color"
                  name="accent_color"
                  value={formik.values.accent_color}
                  onChange={formik.handleChange}
                  style={{ maxWidth: 60, padding: 2 }}
                />
                <Input
                  type="text"
                  name="accent_color"
                  value={formik.values.accent_color}
                  onChange={formik.handleChange}
                  placeholder="#ED1C24"
                  style={{ maxWidth: 140 }}
                />
              </div>
            </FormGroup>
            <FormGroup check className="mt-2">
              <Input
                type="checkbox"
                name="is_active"
                checked={formik.values.is_active}
                onChange={formik.handleChange}
                id="pad-active"
              />
              <Label check for="pad-active">
                Active
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={creating || editing}>
              {creating || editing ? "Saving..." : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </PageWrapper>
  )
}

export default CompanyPadList
